import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PALETTE = ["#6750A4", "#0F5027", "#7D5260", "#B3261E", "#386A20", "#625B71"];
const CADENCES = [
  { value: "daily", label: "Every day" },
  { value: "weekdays", label: "Weekdays only" },
  { value: "times_per_week", label: "Some days a week" },
];

export default function HealthHabits() {
  const [habits, setHabits] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/habits");
    setHabits(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (h) => {
    if (!confirm(`Archive "${h.name}"? Its history is kept.`)) return;
    await api.delete(`/habits/${h.id}`);
    toast.success("Archived");
    load();
  };

  return (
    <div data-testid="health-habits" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-surface-on">Your habits</h3>
        <button
          data-testid="habit-add-open"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-medium px-5 py-2.5 active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md"
        ><Plus className="w-4 h-4" /> Add habit</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="habits-grid">
        {habits.map((h) => (
          <div key={h.id} data-testid={`habit-${h.id}`} className="rounded-2xl bg-surface-container p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-medium" style={{ backgroundColor: h.color + "22", color: h.color }}>
                {h.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-lg font-medium text-surface-on truncate">{h.name}</div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`habit-edit-${h.id}`} onClick={() => { setEditing(h); setDialogOpen(true); }} className="p-1.5 rounded-full text-surface-onVariant hover:bg-primary/8 hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`habit-delete-${h.id}`} onClick={() => remove(h)} className="p-1.5 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="text-xs text-surface-onVariant mt-0.5 capitalize">
                  {CADENCES.find((c) => c.value === h.cadence)?.label}{h.time_of_day ? ` · ${h.time_of_day}` : ""}
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-surface-onVariant">30-day rhythm</span>
                    <span className="text-surface-on font-medium tabular-nums" data-testid={`habit-rhythm-${h.id}`}>{h.rhythm_30d}%</span>
                  </div>
                  <div className="h-2 bg-surface-containerHigh rounded-full overflow-hidden">
                    <div className="h-full transition-all duration-500 ease-md" style={{ width: `${h.rhythm_30d}%`, backgroundColor: h.color }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <HabitDialog open={dialogOpen} onOpenChange={setDialogOpen} habit={editing} onSaved={load} />
    </div>
  );
}

function HabitDialog({ open, onOpenChange, habit, onSaved }) {
  const isEdit = !!habit;
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState("daily");
  const [color, setColor] = useState(PALETTE[0]);
  const [tod, setTod] = useState("");
  const [target, setTarget] = useState(3);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (habit) {
      setName(habit.name); setCadence(habit.cadence); setColor(habit.color);
      setTod(habit.time_of_day || ""); setTarget(habit.target_per_week || 3);
    } else {
      setName(""); setCadence("daily"); setColor(PALETTE[0]); setTod(""); setTarget(3);
    }
  }, [open, habit]);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), cadence, color,
        time_of_day: tod || null,
        target_per_week: cadence === "times_per_week" ? parseInt(target) || 3 : null,
        weekdays: cadence === "weekdays" ? [0, 1, 2, 3, 4] : null,
        allow_rest_days: true,
      };
      if (isEdit) await api.patch(`/habits/${habit.id}`, payload);
      else await api.post("/habits", payload);
      toast.success(isEdit ? "Habit updated" : "Habit added");
      onOpenChange(false); onSaved();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="habit-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">{isEdit ? "Edit habit" : "New habit"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-3">
          <Field label="Name"><input data-testid="habit-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" placeholder="e.g. Meditate" required /></Field>
          <div>
            <Label>Cadence</Label>
            <Select value={cadence} onValueChange={setCadence}>
              <SelectTrigger data-testid="habit-cadence" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {CADENCES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {cadence === "times_per_week" && (
            <Field label="Times per week">
              <input data-testid="habit-target" type="number" min="1" max="7" value={target} onChange={(e) => setTarget(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" />
            </Field>
          )}
          <Field label="Time of day (optional)">
            <input data-testid="habit-tod" type="time" value={tod} onChange={(e) => setTod(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" />
          </Field>
          <div>
            <Label>Colour</Label>
            <div className="flex gap-2">
              {PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)}
                        className={cn("w-8 h-8 rounded-full border-2 transition-transform duration-200 ease-md",
                          color === c ? "border-surface-on scale-110" : "border-transparent")}
                        style={{ backgroundColor: c }} aria-label={`Color ${c}`} />
              ))}
            </div>
          </div>
          <button data-testid="habit-save" type="submit" disabled={saving}
                  className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add habit"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Label({ children }) {
  return <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center bg-surface-containerHigh rounded-t-lg border-b-2 border-outline focus-within:border-primary px-3 py-2.5 transition-colors duration-200 ease-md">
        {children}
      </div>
    </div>
  );
}
